import { ErrorMessages, MessageField, SignUpSource } from '@/constants';
import { useGeo, useGlobalState } from '@/hooks';
import { type IUserInfo, LoginMethod } from '@/interfaces';
import { GoogleOAuthProvider } from '@react-oauth/google';
import getConfig from 'next/config';
import { type AnchorHTMLAttributes, type ComponentType, useMemo } from 'react';

import { useAuth } from '@/hooks/useAuth.hook';

import type { AuthenticatePMPayload } from '@/fetchers/umsAPI/umsAuthenticator.fetchers';
import { PmtOnboardingPopupCta } from '@/interfaces/tracking/trackingOnboardingPopup.interfaces';
import { getGaClientId, getSessionId } from '@/modules/pmt/utils/ga4.utils';
import { userInfoModify } from '@/utils';
import GoogleLoginButton, { type GoogleLoginButtonProps } from './GoogleLoginButton';

interface IGoogleLoginProps extends AnchorHTMLAttributes<'a'> {
  buttonText?: string;
  shouldReloadAfterSuccess?: boolean;
  signUpSource?: SignUpSource;
  newsletterOptIn?: boolean;
  customHandleSuccess?: (userInfo: IUserInfo) => void;
  customHandleClick?: () => void;
  as?: string | ComponentType<any>;
}

const { publicRuntimeConfig } = getConfig();

const GoogleLogin: React.FC<IGoogleLoginProps> = ({
  buttonText = 'CONTINUE WITH GOOGLE',
  shouldReloadAfterSuccess,
  customHandleSuccess,
  customHandleClick,
  signUpSource = SignUpSource.Insights,
  newsletterOptIn = true,
}) => {
  const { authPM } = useAuth();
  const { userInfo, isMultiLanguagePage, isFromGetNotifiedBtn, isFromEmailSyncBtn, isFromDelayedShipmentBtn } =
    useGlobalState();
  const { geoInfo } = useGeo();
  const { googleSignInClientId } = publicRuntimeConfig;

  const cta = useMemo(() => {
    switch (true) {
      case isFromGetNotifiedBtn:
        return PmtOnboardingPopupCta.Noti;
      case isFromEmailSyncBtn:
        return PmtOnboardingPopupCta.Login;
      case isFromDelayedShipmentBtn:
        return PmtOnboardingPopupCta.DelayedShipment;
      default:
    }
  }, [isFromGetNotifiedBtn, isFromEmailSyncBtn, isFromDelayedShipmentBtn]);

  const loginButtonProps: GoogleLoginButtonProps = {
    onOAuthError: (error) => {
      console.error({ error });
    },
    onOAuthSuccess: async (response) => {
      const { access_token } = response;

      if (!access_token) {
        throw new Error(ErrorMessages[MessageField.AUTHENTICATE_ERROR].error);
      }

      const systemLoginPayload: AuthenticatePMPayload = {
        mediumOrigin: 'google',
        code: access_token,
        signUpSource,
        newsletterOptIn,
        country: userInfo?.country ?? geoInfo.country,
        gaClientId: getGaClientId(),
        gaSessionId: getSessionId(isMultiLanguagePage),
        eventTimestamp: Date.now().toString(),
        cta,
        authMethod: LoginMethod.Google,
      };
      try {
        const res = await authPM(systemLoginPayload);

        // trigger zapier and send GTM
        const modifiedUserInfo = userInfoModify(res.user_info);
        if (shouldReloadAfterSuccess) {
          location.reload();
        }
        customHandleSuccess?.(modifiedUserInfo); // Change to optional chain: biome rule
      } catch (e) {
        console.error(e);
      }
    },
    redirect_uri: 'postmessage',
    customHandleClick: () => customHandleClick?.(), // Change to optional chain: biome rule
  };

  return (
    <>
      <GoogleOAuthProvider clientId={googleSignInClientId}>
        <GoogleLoginButton {...loginButtonProps}>{buttonText}</GoogleLoginButton>
      </GoogleOAuthProvider>
    </>
  );
};

export default GoogleLogin;
